import { API_BASE_URL } from '../config/api';
import { isVoiceCloudTranscriptionDisabled } from '../voice/killSwitches';
import { resolveVoiceLanguage } from '../voice/resolveVoiceLanguage';
import {
  getPreferredMediaRecorderMimeType,
  isIOS,
  isMobileDevice,
  isRealMobileDevice,
} from './voiceCaptureSupport';

let sharedAudioContext: AudioContext | null = null;
/** Long-lived mic stream on iOS — stop→reacquire re-prompts permission. */
let sharedMicrophoneStream: MediaStream | null = null;

/** Reuse an AudioContext unlocked during the mic tap gesture (required on mobile). */
export function getSharedAudioContext(): AudioContext | null {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') return null;
  return sharedAudioContext;
}

export function getSharedMicrophoneStream(): MediaStream | null {
  if (sharedMicrophoneStream && sharedMicrophoneStream.active) {
    return sharedMicrophoneStream;
  }
  sharedMicrophoneStream = null;
  return null;
}

/** Fully release the shared mic (logout / unmount / explicit deny). */
export function releaseSharedMicrophoneStream(): void {
  if (sharedMicrophoneStream) {
    sharedMicrophoneStream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
    sharedMicrophoneStream = null;
  }
}

const getAudioConstraints = (): boolean | MediaTrackConstraints => {
  // Mobile browsers (iOS Safari, Samsung Internet, Android Chrome) can reject
  // advanced audio constraints — keep the request simple on real phones/tablets.
  if (isIOS() || isRealMobileDevice()) return true;
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  };
};

const requestMicrophoneStream = (): Promise<MediaStream | null> => {
  if (!navigator.mediaDevices?.getUserMedia) return Promise.resolve(null);

  const existing = getSharedMicrophoneStream();
  if (existing) return Promise.resolve(existing);

  return navigator.mediaDevices.getUserMedia({ audio: getAudioConstraints() })
    .then((stream) => {
      // Keep a live stream on iOS so the next tap does not re-prompt.
      if (isIOS()) {
        sharedMicrophoneStream = stream;
      }
      return stream;
    })
    .catch(async (error) => {
      // Retry with the simplest constraint set if advanced options were rejected.
      if (!isIOS()) {
        try {
          return await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
          /* fall through */
        }
      }
      console.warn('Microphone access failed:', error);
      return null;
    });
};

/**
 * Unlock/resume a short-lived AudioContext during a user gesture.
 * Mobile Safari often keeps AudioContext suspended until explicitly resumed.
 */
export function unlockAudioForMobileRecording(): void {
  if (!isMobileDevice()) return;
  const win = window as Window & {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioCtx = win.AudioContext || win.webkitAudioContext;
  if (!AudioCtx) return;

  try {
    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = new AudioCtx();
    }
    if (sharedAudioContext.state === 'suspended') {
      void sharedAudioContext.resume();
    }
  } catch {
    /* ignore — waveform setup will retry later */
  }
}

/**
 * Request microphone access synchronously from a user gesture when possible.
 * On iOS, reuses a live shared stream so permission is not re-prompted.
 */
export function requestMicrophoneAccess(existingStream?: MediaStream | null): Promise<MediaStream | null> {
  if (existingStream?.active) {
    if (isIOS()) sharedMicrophoneStream = existingStream;
    return Promise.resolve(existingStream);
  }
  const shared = getSharedMicrophoneStream();
  if (shared) return Promise.resolve(shared);
  unlockAudioForMobileRecording();
  return requestMicrophoneStream();
}

export async function warmUpMicrophone(existingStream?: MediaStream | null): Promise<MediaStream | null> {
  if (existingStream?.active) {
    if (isIOS()) sharedMicrophoneStream = existingStream;
    return existingStream;
  }
  return requestMicrophoneStream();
}

const TRANSCRIBE_ENDPOINTS = ['/voice/transcribe', '/voice/process'] as const;

export interface VoiceTranscriptionErrorDetails {
  endpoint: string;
  status: number;
  serverError: string;
  audioSize?: number;
  audioType?: string;
  businessId?: string;
}

export class VoiceTranscriptionError extends Error {
  readonly details: VoiceTranscriptionErrorDetails;

  constructor(details: VoiceTranscriptionErrorDetails) {
    super(details.serverError);
    this.name = 'VoiceTranscriptionError';
    this.details = details;
  }
}

/** Multi-line message users can screenshot and share with support. */
export const formatVoiceTranscriptionError = (details: VoiceTranscriptionErrorDetails): string => {
  const lines = [
    'Voice mic failed — share this error:',
    `Error: ${details.serverError}`,
    `Endpoint: ${details.endpoint}`,
    `HTTP status: ${details.status}`,
  ];

  if (details.audioType || details.audioSize) {
    const sizeKb = details.audioSize ? `${Math.max(1, Math.round(details.audioSize / 1024))}KB` : 'unknown size';
    lines.push(`Recording: ${details.audioType || 'unknown type'}, ${sizeKb}`);
  }

  if (details.businessId) {
    lines.push(`Business ID: ${details.businessId}`);
  }

  if (/not yet implemented/i.test(details.serverError)) {
    lines.push('Hint: server is missing the latest voice transcription update — redeploy backend.');
  }

  return lines.join('\n');
};

const getAuthToken = (): string | null =>
  localStorage.getItem('token') || localStorage.getItem('auth_token');

const parseTranscriptionResponse = (data: Record<string, unknown>): string => {
  const text = typeof data.text === 'string' ? data.text.trim() : '';
  return text;
};

const extractServerError = (data: Record<string, unknown>, status: number): string => {
  if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  return `Transcription failed (${status})`;
};

async function postAudioForTranscription(
  endpoint: string,
  formData: FormData,
  token: string,
  businessId?: string,
): Promise<Response> {
  const query = businessId ? `?business_id=${encodeURIComponent(businessId)}` : '';
  try {
    return await fetch(`${API_BASE_URL}${endpoint}${query}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  } catch {
    // Network-level failure (offline, DNS, CORS) — route into the same
    // fallback chain as a missing endpoint so browser speech can take over.
    throw new Error('Transcription service unavailable');
  }
}

export async function transcribeAudioBlob(blob: Blob, language?: string): Promise<string> {
  if (isVoiceCloudTranscriptionDisabled()) {
    throw new Error(
      'Cloud voice transcription is temporarily disabled. Try again later or type your command.'
    );
  }

  const token = getAuthToken();
  if (!token) {
    throw new Error('Please log in to use voice commands.');
  }

  const extension = blob.type.includes('mp4') || blob.type.includes('aac') || blob.type.includes('mpeg')
    ? 'm4a'
    : blob.type.includes('ogg')
      ? 'ogg'
      : 'webm';
  let lastError = 'Transcription failed';
  const businessIdStr = localStorage.getItem('businessId');
  const resolvedLanguage = resolveVoiceLanguage(
    language ? { userLanguage: language } : undefined
  );

  for (const endpoint of TRANSCRIBE_ENDPOINTS) {
    const formData = new FormData();
    const mimeType = blob.type || (extension === 'm4a' ? 'audio/mp4' : `audio/${extension}`);
    formData.append('audio', new File([blob], `recording.${extension}`, { type: mimeType }));
    formData.append('language', resolvedLanguage.appLanguage);
    formData.append('locale', resolvedLanguage.locale);
    if (businessIdStr) formData.append('business_id', businessIdStr);

    const response = await postAudioForTranscription(endpoint, formData, token, businessIdStr || undefined);

    if (response.status === 404) {
      lastError = 'Transcription service unavailable';
      continue;
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));

    if (response.status === 429) {
      throw new VoiceTranscriptionError({
        endpoint,
        status: response.status,
        serverError: 'Too many voice requests. Please wait a moment and try again.',
        audioSize: blob.size,
        audioType: mimeType,
        businessId: businessIdStr || undefined,
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        lastError = 'Transcription service unavailable';
        continue;
      }

      throw new VoiceTranscriptionError({
        endpoint,
        status: response.status,
        serverError: extractServerError(data, response.status),
        audioSize: blob.size,
        audioType: mimeType,
        businessId: businessIdStr || undefined,
      });
    }

    if (data.success === false) {
      throw new VoiceTranscriptionError({
        endpoint,
        status: response.status,
        serverError: extractServerError(data, response.status),
        audioSize: blob.size,
        audioType: mimeType,
        businessId: businessIdStr || undefined,
      });
    }

    const text = parseTranscriptionResponse(data);
    if (text.length > 1) return text;

    throw new VoiceTranscriptionError({
      endpoint,
      status: response.status,
      serverError: 'No speech detected in the recording',
      audioSize: blob.size,
      audioType: mimeType,
      businessId: businessIdStr || undefined,
    });
  }

  throw new VoiceTranscriptionError({
    endpoint: TRANSCRIBE_ENDPOINTS.join(' → '),
    status: 404,
    serverError: lastError,
    audioSize: blob.size,
    audioType: blob.type,
    businessId: businessIdStr || undefined,
  });
}

export function createMediaRecorder(
  stream: MediaStream,
  onData: (chunk: Blob) => void,
): MediaRecorder {
  const mimeType = getPreferredMediaRecorderMimeType();
  let recorder: MediaRecorder;
  try {
    recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
  } catch {
    recorder = new MediaRecorder(stream);
  }

  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) onData(event.data);
  });

  return recorder;
}

export function isTranscriptionUnavailableError(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('route not found')
    || normalized.includes('transcription service unavailable')
    || normalized.includes('audio conversion is unavailable')
    || normalized.includes('not yet implemented')
    || normalized.includes('no llm provider')
    || normalized.includes('no openai api key')
    || normalized.includes('api key')
    || normalized.includes('incorrect api key')
    || normalized.includes('not configured for audio')
    || normalized.includes('audio/transcriptions')
    || normalized.includes('whisper')
  );
}

/** Resolve the user's language code for server-side Whisper transcription. */
export const resolveVoiceLanguageCode = (): string =>
  resolveVoiceLanguage().appLanguage;
